import { fmtLength, parseLengthToFeet, isMetric } from "./units";

// Construction dimensions are written in feet and inches — 13'-7 5/8" — not as decimal feet (13.611953951866894). This is the one
// place that formats and parses them. Stored values stay decimal feet (everything downstream — areas, quantities, pricing — is
// feet); a dimension a person types or draws is snapped to the nearest 1/16" so what is stored is exactly what is shown.

export const SIXTEENTHS_PER_FT = 12 * 16;   // 192

/** Decimal feet snapped to the nearest 1/16 inch (null / non-numbers pass through as null). */
export const snapToSixteenth = (ft) => {
  if (ft === null || ft === undefined || ft === "") return null;
  const n = +ft;
  return Number.isFinite(n) ? Math.round(n * SIXTEENTHS_PER_FT) / SIXTEENTHS_PER_FT : null;
};

const gcd = (a, b) => (b ? gcd(b, a % b) : a);

/** 13.6354 -> `13'-7 5/8"`; whole inches `13'-7"`; no inches `13'-0"`. `denom` is the finest fraction shown (16 = sixteenths, 8 = eighths, 1 = whole inches). */
export function formatFeetInches(ft, { denom = 16 } = {}) {
  if (ft === null || ft === undefined || ft === "" || !Number.isFinite(+ft)) return "—";
  const v = +ft, sign = v < 0 ? "-" : "";
  const total = Math.round(Math.abs(v) * 12 * denom);
  const feet = Math.floor(total / (12 * denom));
  const rem = total % (12 * denom);
  const inches = Math.floor(rem / denom);
  const frac = rem % denom;
  let fracText = "";
  if (frac) { const g = gcd(frac, denom); fracText = ` ${frac / g}/${denom / g}`; }
  return `${sign}${feet}'-${inches}${fracText}"`;
}

const FRAC = /^(\d+)\s*\/\s*(\d+)$/;
// "7", "7.5", "7 5/8", "5/8", "" -> inches (number) or null when it is not an inches expression
function parseInches(text) {
  const t = text.trim();
  if (t === "") return 0;
  let m = t.match(/^(\d+(?:\.\d+)?)$/);
  if (m) return parseFloat(m[1]);
  m = t.match(FRAC);
  if (m) return +m[2] ? +m[1] / +m[2] : null;
  m = t.match(/^(\d+)\s+(\d+)\s*\/\s*(\d+)$/);
  if (m) return +m[3] ? +m[1] + +m[2] / +m[3] : null;
  return null;
}

/**
 * What an estimator types -> decimal feet, or null when it isn't a dimension. Accepts `13'-7 5/8"`, `13' 7"`, `13'7`, `13-7`,
 * `13 ft 7 in`, `7 5/8"` (inches only), `162"`, `13.5'`, `13.5` (a bare number is feet) and curly marks (′ ″).
 */
export function parseFeetInches(text) {
  let s = String(text ?? "").trim().toLowerCase().replace(/[′’]/g, "'").replace(/[″”]/g, '"').replace(/,/g, "");
  if (!s) return null;
  let sign = 1;
  if (s.startsWith("-")) { sign = -1; s = s.slice(1).trim(); }
  s = s.replace(/\bfeet\b|\bfoot\b|\bft\b/g, "'").replace(/\binches\b|\binch\b|\bin\b/g, '"');

  // feet mark present: 13'-7 5/8" , 13' 7" , 13'
  const tick = s.indexOf("'");
  if (tick >= 0) {
    const feetText = s.slice(0, tick).trim();
    if (!/^\d+(?:\.\d+)?$/.test(feetText)) return null;
    const rest = s.slice(tick + 1).replace(/^[\s-]+/, "").replace(/"\s*$/, "").trim();
    const inches = parseInches(rest);
    if (inches === null) return null;
    return sign * (parseFloat(feetText) + inches / 12);
  }
  // inches only: 7 5/8" , 162"
  if (s.endsWith('"')) {
    const inches = parseInches(s.slice(0, -1));
    return inches === null ? null : sign * inches / 12;
  }
  // 13-7 / 13-7 5/8: hyphen between whole feet and inches
  const dash = s.match(/^(\d+)\s*-\s*(.+)$/);
  if (dash) {
    const inches = parseInches(dash[2]);
    return inches === null ? null : sign * (parseInt(dash[1], 10) + inches / 12);
  }
  // a bare number is feet
  if (/^\d+(?:\.\d+)?$/.test(s) || /^\.\d+$/.test(s)) return sign * parseFloat(s);
  return null;
}

/** A dimension for display in the viewer's units: feet-inches for imperial, the units.js metric form otherwise. */
export const dimText = (ft, units = "imperial", opts) => (isMetric(units) ? fmtLength(ft, units) : formatFeetInches(ft, opts));

/** Text -> decimal feet in the viewer's units (imperial feet-inches, or metric via units.js). null = not a dimension. */
export const parseDim = (text, units = "imperial") => (isMetric(units) ? parseLengthToFeet(text, units) : parseFeetInches(text));
